import clsx from "clsx";
import { AnimatePresence, type AnimationProps, motion, useReducedMotion } from "framer-motion";
import React, { useState } from "react";
import { FormattedMessage, type MessageDescriptor } from "react-intl";

import { PATIENT_MAX_FILESIZE } from "~/common/constants";
import FileUpload from "~/components/file_upload";
import { LabelRequiredIndicator } from "~/components/ui/label";

export function PatientNewFiles({
  required,
  isVisible = true,
  smartPrescriptionMode = false,
  progressBarColor,
  titleId,
  subtitleId,
  children,
}: {
  required?: boolean;
  isVisible?: boolean;
  smartPrescriptionMode?: boolean;
  progressBarColor?: string;
  titleId?: MessageDescriptor["id"];
  subtitleId?: MessageDescriptor["id"];
  children?: React.ReactNode;
}) {
  const [numberOfUploads, setNumberOfUploads] = useState(0);

  return (
    <AnimatePatientNewFilesPresence isVisible={isVisible}>
      <div id="extra-media-block" className="form-body">
        <h4 id="validation-files" className="block" style={{ fontWeight: 900 }}>
          <FormattedMessage
            id={titleId ?? (smartPrescriptionMode ? "UPLOAD_FILES_SMART" : "UPLOAD_FILES")}
          />
          {required ? <LabelRequiredIndicator /> : null}
        </h4>

        {subtitleId ? (
          <p className="tw-m-0 tw-mb-5 tw-text-sm tw-leading-5 tw-text-muted-foreground">
            <FormattedMessage id={subtitleId} />
          </p>
        ) : null}

        <div className={clsx(smartPrescriptionMode && "patient-smart-files__list")}>
          {Array.from({ length: numberOfUploads + 1 }).map((_, uploadId) => (
            <FileUpload
              key={`recipe_${uploadId}`}
              id={`recipe_${uploadId}`}
              patient_id={0}
              semantics="recipe"
              multiple
              nextBatchId={(offset) => `recipe_${uploadId + offset}`}
              onFileUploadEnd={() =>
                setNumberOfUploads((prevNumberOfUploads) => prevNumberOfUploads + 1)
              }
              ariaLabelledBy="validation-files"
              maxFileSize={PATIENT_MAX_FILESIZE}
              progressBarColor={progressBarColor}
            />
          ))}
        </div>

        {children}
      </div>
    </AnimatePatientNewFilesPresence>
  );
}

function AnimatePatientNewFilesPresence({
  children,
  isVisible,
}: React.PropsWithChildren<{ isVisible: boolean }>) {
  const shouldReduceMotion = useReducedMotion();

  const animationProps: AnimationProps = {
    initial: { height: 0 },
    animate: { height: "auto", transition: { duration: 0.5 } },
    exit: { height: 0 },
  };

  return (
    <AnimatePresence>
      {isVisible ? (
        <motion.div
          // PatientNewFiles h4 adds a margin-top and padding-top that causes
          // the extra vertical space. Had to remove it from here.
          className="tw-overflow-hidden [&_h4]:tw-mt-0 [&_h4]:tw-pt-0"
          {...(shouldReduceMotion ? {} : animationProps)}
        >
          {children}
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}
